import type { CaseFileListRow, MatterFile, MatterSessionType } from "../contract"
import { shortDate } from "../answer/format"
import { FEATURE_LABEL, featureOf, type Feature } from "./features"

export { FEATURE_LABEL, featureOf, type Feature }

/** The three sentences shown on the empty state and the first-run popover. */
export const MATTER_INSTRUCTION = {
  headline: "Keep everything for one case together in a workspace.",
  example: 'Example: create "Acme Traders" and drop in the notice, replies and invoices once.',
  body: "Every search, OIO reply or OIA appeal you start inside it can read all of them; Nira picks the files it needs and asks you if something is missing.",
}

/** A Nira search is Research: its session does not say whether it began on a notice or an order. */
export const TAG_LABEL: Record<MatterSessionType, string> = {
  nira: FEATURE_LABEL.research,
  oio: FEATURE_LABEL.oio,
  oia: FEATURE_LABEL.oia,
}

const files = (n: number) => (n === 1 ? "file" : "files")

/** "added in OIO draft · 12 Oct"; files added on the Matter carry no date. */
export function originLabel(origin: MatterFile["origin"]): string {
  if (origin.type === "matter") return "added on the workspace"
  const from = { nira: "Nira search", oio: "OIO draft", oia: "OIA draft" }[origin.type]
  // shortDate gives "12 Oct 2026"; the copy shows the day and month only.
  const day = shortDate(origin.added_at).replace(/ \d{4}$/, "")
  return `added in ${from} · ${day}`
}

/** Copy for the move-in and move-out confirmation. */
export function moveCopy(p: {
  kind: "in" | "out"
  type: MatterSessionType
  matterTitle: string
  matterFiles: number
  sessionFiles: number
}): string {
  const who = p.type === "nira" ? "This search" : "This draft"
  if (p.kind === "out") return `${who} will only see its own ${p.sessionFiles} ${files(p.sessionFiles)}`
  const join = p.sessionFiles === 1 ? "joins" : "join"
  return `${who} will see ${p.matterTitle}'s ${p.matterFiles} ${files(p.matterFiles)}; its ${p.sessionFiles} ${files(p.sessionFiles)} ${join} the workspace`
}

/** The id of an open Matter whose title matches, ignoring case and surrounding spaces. */
export function matchOpenMatter(title: string, rows: Pick<CaseFileListRow, "id" | "title" | "closed">[]): string | null {
  const wanted = title.trim().toLowerCase()
  const hit = rows.find((r) => !r.closed && r.title.trim().toLowerCase() === wanted)
  return hit ? hit.id : null
}

/** "Using Acme Traders's 7 files" (singular for one file). */
export function usingLine(title: string, n: number): string {
  return `Using ${title}'s ${n} ${files(n)}`
}
